import type { OnBehalfContext } from './cm-sites';
import { ReparoApi } from './index';
import type { ReceivableOrder, ReceiveResult, VendorCatalog } from './vendor-portal';

// ─── Enums ──────────────────────────────────────────────────────────────────

export type StnStatus = 'DRAFT' | 'APPROVED' | 'DISPATCHED' | 'IN_TRANSIT' | 'RECEIVED' | 'CANCELLED';
export type GrnStatus = 'RECEIVED' | 'QC_DONE' | 'PUTAWAY_DONE';
export type GrnSourceType = 'PURCHASE_ORDER' | 'MATERIAL_ORDER' | 'STN';

// ─── Shelf Location types ────────────────────────────────────────────────────

export type ShelfLocation = {
    id: string;
    merchantId: string;
    warehouseId: string;
    code: string;
    name: string | null;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
};

export type CreateShelfLocationInput = {
    warehouseId: string;
    code: string;
    name?: string;
};

export type UpdateShelfLocationInput = {
    name?: string;
    isActive?: boolean;
};

// ─── STN types ───────────────────────────────────────────────────────────────

export type StnItem = {
    id: string;
    stnId: string;
    itemId: string;
    itemType: string;
    plannedQty: number;
    dispatchedQty: number | null;
    receivedQty: number | null;
    /** Shipped quantity the source dropped from this line (Amazon FBA); its quantities are 0 while set. */
    removedQty?: number | null;
    /** Detail view: the product / material name from the catalogue. */
    itemName?: string | null;
};

export type Stn = {
    id: string;
    code: string;
    merchantId: string;
    fromNodeId: string;
    toNodeId: string;
    itemType: string;
    workOrderId: string | null;
    replenishmentPlanId: string | null;
    status: StnStatus;
    isManual: boolean;
    /** The sale order this STN ships, when it was raised from one (a B2B customer dispatch). */
    saleOrderCode?: string | null;
    notes: string | null;
    createdBy: string | null;
    approvedAt: string | null;
    dispatchedAt: string | null;
    receivedAt: string | null;
    /** The day the note is to leave; null = as soon as it is approved. */
    plannedDispatchDate?: string | null;
    expectedArrivalDate?: string | null;
    /** The WMS gate pass this STN was pushed as, once pushed. */
    wmsGatePassCode?: string | null;
    wmsPushedAt?: string | null;
    transferKind?: 'INTERNAL' | 'CHANNEL' | 'VENDOR' | null;
    sourceSystem?: string | null;
    /** The source system's own status word (Amazon's WORKING / IN_TRANSIT / RECEIVING). Null on platform STNs. */
    externalStatus?: string | null;
    /** Who moves a mirrored shipment (Amazon: USE_YOUR_OWN_CARRIER / AMAZON_PARTNERED_CARRIER). */
    transportMode?: string | null;
    /** Decorated: node display names, the kind of transfer, and where the STN came from. */
    fromNodeName?: string | null;
    toNodeName?: string | null;
    toNodeKind?: 'INTERNAL' | 'CHANNEL' | 'VENDOR' | null;
    /** The contract-manufacturer site this transfer lands at, when it goes to one. */
    toSiteId?: string | null;
    source?: string;
    /** List view only: units sent / received across the STN's lines; null until any are. */
    totalDispatchedQty?: number | null;
    /** Planned units summed over the lines — scheduled before dispatch. */
    totalPlannedQty?: number | null;
    totalReceivedQty?: number | null;
    createdAt: string;
    updatedAt: string;
    items?: StnItem[];
    warnings?: Array<{ itemId: string; available: number; requested: number }>;
};

export type CreateStnItemInput = { itemId: string; plannedQty: number; batchId?: string };

export type CreateStnInput = {
    fromNodeId: string;
    /** INTERNAL (warehouse) | CHANNEL (sales vendor) | VENDOR (purchase vendor). */
    transferKind?: 'INTERNAL' | 'CHANNEL' | 'VENDOR';
    toNodeId: string;
    itemType: string;
    workOrderId?: string;
    replenishmentPlanId?: string;
    notes?: string;
    /** yyyy-MM-dd the note is to leave; omitted = as soon as it is approved. */
    plannedDispatchDate?: string;
    expectedArrivalDate?: string;
    items: CreateStnItemInput[];
};

export type ReceiveStnItemInput = { stnItemId: string; receivedQty: number };
export type ReceiveStnInput = { items: ReceiveStnItemInput[] };

export type StnListParams = {
    page?: number;
    take?: number;
    status?: StnStatus;
    fromNodeId?: string;
    toNodeId?: string;
    workOrderId?: string;
    dateFrom?: string;
    dateTo?: string;
    /** PLATFORM or an external source system (AMAZON_SP_API). */
    source?: string;
    /** Multi-select filters (the Stock Transfers page's filter chips). */
    statuses?: StnStatus[];
    sources?: string[];
    fromNodeIds?: string[];
    toNodeIds?: string[];
    /** Only notes carrying one of these SKUs; unit totals then cover those lines only. */
    itemIds?: string[];
    /** Matches the STN code or its WMS gate-pass code. */
    search?: string;
};

export type StnListResponse = {
    data: Stn[];
    meta: { page: number; take: number; itemCount: number; pageCount: number; hasPreviousPage: boolean; hasNextPage: boolean };
};

/** One SKU over its open notes (Approved / Dispatched / In Transit), whatever their date. */
export type StnBySkuRow = {
    itemId: string;
    itemType: string;
    itemName: string | null;
    noteCount: number;
    plannedQty: number;
    dispatchedQty: number | null;
    receivedQty: number | null;
    /** Dispatched (planned while not yet dispatched) minus received. */
    openQty: number;
    /** Earliest ship-by across open notes not yet dispatched; yyyy-MM-dd. */
    nextShipBy: string | null;
    /** Earliest expected arrival across the open notes; yyyy-MM-dd. */
    nextExpectedArrival: string | null;
};
export type StnBySkuListResponse = { data: StnBySkuRow[]; meta: StnListResponse['meta'] };

// ─── GRN types ───────────────────────────────────────────────────────────────

export type GrnItemShelfAssignment = {
    id: string;
    shelfLocationId: string;
    quantity: number;
    createdAt: string;
};

export type GrnItem = {
    id: string;
    grnId: string;
    itemId: string;
    orderedQty: number;
    receivedQty: number;
    qcPassedQty: number | null;
    qcFailedQty: number | null;
    putawayQty: number | null;
    stockEventId: string | null;
    qcRejectionEventId: string | null;
    shelfAssignments?: GrnItemShelfAssignment[];
    /** What became of the quantity turned away at the gate. Detail responses only. */
    rejection?: { movementType: string | null; reasonCode: string | null; notes: string | null };
};

export type Grn = {
    id: string;
    code: string;
    merchantId: string;
    sourceWarehouseId: string;
    sourceType: GrnSourceType;
    sourceId: string | null;
    /** The source by its name: a transfer's code (sourceId holds its id), else sourceId. */
    sourceLabel?: string | null;
    itemType: string;
    status: GrnStatus;
    receivedDate: string;
    createdBy: string;
    qcCompletedAt: string | null;
    putawayCompletedAt: string | null;
    itemCount?: number;
    createdAt: string;
    updatedAt: string;
    items?: GrnItem[];
    wmsAlreadyReceivedWarning?: boolean;
    /** Its movements were undone; the GRN stays as the record. */
    reversed?: boolean;
    /** Who received the goods, as named on the receipt. Detail responses only. */
    receivedBy?: string | null;
    /** The delivery note (challan) the goods came on. Detail responses only. */
    deliveryNote?: string | null;
};

export type CreateGrnItemInput = { itemId: string; orderedQty: number; receivedQty: number };
export type CreateGrnInput = {
    sourceWarehouseId: string;
    sourceType: GrnSourceType;
    sourceId?: string;
    itemType: string;
    receivedDate: string;
    /** The delivery note (challan) the goods came on. */
    reference?: string;
    /** Who received the goods. */
    receivedBy?: string;
    items: CreateGrnItemInput[];
};

export type SubmitQcItemInput = { grnItemId: string; qcPassedQty: number; qcFailedQty: number };
export type SubmitQcInput = { items: SubmitQcItemInput[] };

export type ShelfAssignmentInput = { shelfLocationId: string; quantity: number };
export type SubmitPutawayItemInput = { grnItemId: string; assignments: ShelfAssignmentInput[] };
export type SubmitPutawayInput = { items: SubmitPutawayItemInput[] };

export type GrnListParams = {
    page?: number;
    take?: number;
    status?: GrnStatus;
    sourceType?: GrnSourceType;
    sourceWarehouseId?: string;
    dateFrom?: string;
    dateTo?: string;
};

export type GrnListResponse = {
    data: Grn[];
    meta: { page: number; take: number; itemCount: number; pageCount: number; hasPreviousPage: boolean; hasNextPage: boolean };
};

export type GrnPrefillItem = {
    itemId: string;
    itemName: string;
    sku?: string | null;
    unit: string | null;
    orderedQty: number;
};

// ─── Shelf Location API ───────────────────────────────────────────────────────

export const listShelfLocations = (warehouseId: string): Promise<ShelfLocation[]> =>
    ReparoApi<ShelfLocation[]>({ method: 'GET', url: `/warehouse-ops/shelf-locations?warehouseId=${warehouseId}` });

export const createShelfLocation = (payload: CreateShelfLocationInput): Promise<ShelfLocation> =>
    ReparoApi<ShelfLocation>({ method: 'POST', url: '/warehouse-ops/shelf-locations', body: JSON.stringify(payload) });

export const updateShelfLocation = (id: string, payload: UpdateShelfLocationInput): Promise<ShelfLocation> =>
    ReparoApi<ShelfLocation>({ method: 'PATCH', url: `/warehouse-ops/shelf-locations/${id}`, body: JSON.stringify(payload) });

export const deleteShelfLocation = (id: string): Promise<void> =>
    ReparoApi<void>({ method: 'DELETE', url: `/warehouse-ops/shelf-locations/${id}` });

// ─── STN API ─────────────────────────────────────────────────────────────────

export const createStn = (payload: CreateStnInput): Promise<Stn> =>
    ReparoApi<Stn>({ method: 'POST', url: '/warehouse-ops/stns', body: JSON.stringify(payload) });

// Source/destination picker for STNs. Unlike the Work Order picker (INTERNAL only),
// an STN can move stock between ANY warehouse the merchant controls — internal DCs,
// retail, and channel FCs (Amazon/Zepto/etc.) — so no `types` filter is applied.
// The backend already scopes /warehouses/list to the authenticated merchant.
export type WarehousePickItem = { warehouseId: string; name: string; type: string; facilityId?: string | null; vendorId?: string | null };

export const listWarehousesForStn = async (): Promise<WarehousePickItem[]> => {
    const resp = await ReparoApi<{ data: Array<{ warehouseId: string; name: string; type: string; facilityId?: string | null; vendorId?: string | null }> }>({
        method: 'POST',
        url: '/warehouses/list',
        body: JSON.stringify({ fetchAll: true }),
    });
    return (resp?.data ?? []).map((w) => ({ warehouseId: w.warehouseId, name: w.name, type: w.type, facilityId: w.facilityId ?? null, vendorId: w.vendorId ?? null }));
};

const stnListQueryString = (params: StnListParams): string => {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', String(params.page));
    if (params.take) qs.set('take', String(params.take));
    if (params.status) qs.set('status', params.status);
    if (params.fromNodeId) qs.set('fromNodeId', params.fromNodeId);
    if (params.source) qs.set('source', params.source);
    if (params.toNodeId) qs.set('toNodeId', params.toNodeId);
    if (params.workOrderId) qs.set('workOrderId', params.workOrderId);
    if (params.dateFrom) qs.set('dateFrom', params.dateFrom);
    if (params.dateTo) qs.set('dateTo', params.dateTo);
    params.statuses?.forEach((status) => qs.append('statuses', status));
    params.sources?.forEach((source) => qs.append('sources', source));
    params.fromNodeIds?.forEach((id) => qs.append('fromNodeIds', id));
    params.toNodeIds?.forEach((id) => qs.append('toNodeIds', id));
    params.itemIds?.forEach((id) => qs.append('itemIds', id));
    if (params.search?.trim()) qs.set('search', params.search.trim());
    return qs.toString();
};

export const listStns = (params: StnListParams = {}): Promise<StnListResponse> => {
    const q = stnListQueryString(params);
    return ReparoApi<StnListResponse>({ method: 'GET', url: `/warehouse-ops/stns${q ? `?${q}` : ''}` });
};

/** The API pages at 50; this walks every page for a download or a panel that shows the whole set. */
const PAGE_WALK_SIZE = 50;
/** 2,000 rows is far past any one brand's open book; a stop, not a limit. */
const PAGE_WALK_MAX_PAGES = 40;
export const walkPages = async <T,>(
    fetchPage: (page: number, take: number) => Promise<{ data: T[]; meta: { hasNextPage: boolean } }>,
): Promise<T[]> => {
    const rows: T[] = [];
    for (let page = 1; page <= PAGE_WALK_MAX_PAGES; page++) {
        const res = await fetchPage(page, PAGE_WALK_SIZE);
        rows.push(...res.data);
        if (!res.meta.hasNextPage) break;
    }
    return rows;
};

/** Every note matching the filters, across pages. */
export const listAllStns = (params: Omit<StnListParams, 'page' | 'take'> = {}): Promise<Stn[]> =>
    walkPages((page, take) => listStns({ ...params, page, take }));

/**
 * The list rolled up by SKU over open notes only. Takes the same parameters; the server
 * ignores the date range and status filters on this read.
 */
export const listStnsBySku = (params: StnListParams = {}): Promise<StnBySkuListResponse> => {
    const q = stnListQueryString(params);
    return ReparoApi<StnBySkuListResponse>({ method: 'GET', url: `/warehouse-ops/stns/by-sku${q ? `?${q}` : ''}` });
};

/** Every SKU row matching the filters, across pages. */
export const listAllStnsBySku = (params: Omit<StnListParams, 'page' | 'take'> = {}): Promise<StnBySkuRow[]> =>
    walkPages((page, take) => listStnsBySku({ ...params, page, take }));

/** Sources that have written a note for this merchant (PLATFORM, AMAZON_SP_API, ...), Platform first. */
export const listStnSources = (): Promise<string[]> =>
    ReparoApi<string[]>({ method: 'GET', url: '/warehouse-ops/stns/sources' });

export const getStn = (id: string): Promise<Stn> =>
    ReparoApi<Stn>({ method: 'GET', url: `/warehouse-ops/stns/${id}` });

export const pushStnToWms = (id: string): Promise<Stn> =>
    ReparoApi<Stn>({ method: 'POST', url: `/warehouse-ops/stns/${id}/push-to-wms`, body: JSON.stringify({}) });

export const approveStn = (id: string): Promise<Stn> =>
    ReparoApi<Stn>({ method: 'PATCH', url: `/warehouse-ops/stns/${id}/approve` });

export const dispatchStn = (id: string): Promise<Stn> =>
    ReparoApi<Stn>({ method: 'PATCH', url: `/warehouse-ops/stns/${id}/dispatch` });

export const receiveStn = (id: string, payload: ReceiveStnInput): Promise<Stn> =>
    ReparoApi<Stn>({ method: 'PATCH', url: `/warehouse-ops/stns/${id}/receive`, body: JSON.stringify(payload) });

export const cancelStn = (id: string): Promise<Stn> =>
    ReparoApi<Stn>({ method: 'PATCH', url: `/warehouse-ops/stns/${id}/cancel` });

// ─── GRN API ─────────────────────────────────────────────────────────────────

export const getAvailablePOs = (): Promise<Array<{ id: string; label: string }>> =>
    ReparoApi<Array<{ id: string; label: string }>>({ method: 'GET', url: '/warehouse-ops/grns/available-pos' });

/** A dispatched transfer with no GRN yet: what it carries and where it was sent. */
export type AvailableStn = { id: string; label: string; itemType: string; toNodeId: string; fromName: string };

export const getAvailableSTNs = (): Promise<AvailableStn[]> =>
    ReparoApi<AvailableStn[]>({ method: 'GET', url: '/warehouse-ops/grns/available-stns' });

export const getGrnPrefill = (sourceType: GrnSourceType, sourceId: string): Promise<GrnPrefillItem[]> =>
    ReparoApi<GrnPrefillItem[]>({ method: 'GET', url: `/warehouse-ops/grns/prefill?sourceType=${sourceType}&sourceId=${encodeURIComponent(sourceId)}` });

export const createGrn = (payload: CreateGrnInput): Promise<Grn> =>
    ReparoApi<Grn>({ method: 'POST', url: '/warehouse-ops/grns', body: JSON.stringify(payload) });

export const listGrns = (params: GrnListParams = {}): Promise<GrnListResponse> => {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', String(params.page));
    if (params.take) qs.set('take', String(params.take));
    if (params.status) qs.set('status', params.status);
    if (params.sourceType) qs.set('sourceType', params.sourceType);
    if (params.sourceWarehouseId) qs.set('sourceWarehouseId', params.sourceWarehouseId);
    if (params.dateFrom) qs.set('dateFrom', params.dateFrom);
    if (params.dateTo) qs.set('dateTo', params.dateTo);
    const q = qs.toString();
    return ReparoApi<GrnListResponse>({ method: 'GET', url: `/warehouse-ops/grns${q ? `?${q}` : ''}` });
};

export const getGrn = (id: string): Promise<Grn> =>
    ReparoApi<Grn>({ method: 'GET', url: `/warehouse-ops/grns/${id}` });

export const submitGrnQc = (id: string, payload: SubmitQcInput): Promise<Grn> =>
    ReparoApi<Grn>({ method: 'PATCH', url: `/warehouse-ops/grns/${id}/qc`, body: JSON.stringify(payload) });

export const submitGrnPutaway = (id: string, payload: SubmitPutawayInput): Promise<Grn> =>
    ReparoApi<Grn>({ method: 'PATCH', url: `/warehouse-ops/grns/${id}/putaway`, body: JSON.stringify(payload) });

// ─── Material stores: RM/PM into the brand's own warehouses ─────────────────

export type MaterialStore = { warehouseId: string; name: string };

/** The brand's material stores. Empty when none is marked or Warehouse Ops is off. */
export const listMaterialStores = (): Promise<MaterialStore[]> =>
    ReparoApi<MaterialStore[]>({ method: 'GET', url: '/warehouse-ops/material-stores' });

export const getStoreCatalog = (warehouseId: string): Promise<VendorCatalog> =>
    ReparoApi<VendorCatalog>({ method: 'GET', url: `/warehouse-ops/material-stores/${warehouseId}/catalog` });

export const getStoreReceivableOrders = (warehouseId: string): Promise<ReceivableOrder[]> =>
    ReparoApi<ReceivableOrder[]>({ method: 'GET', url: `/warehouse-ops/material-stores/${warehouseId}/receivable-orders` });

/** The same receipt the factories post, filed as a Warehouse Ops GRN. `reportedBy` is who received it. */
export const receiveIntoStore = (
    context: OnBehalfContext,
    receipt: Record<string, unknown>,
): Promise<ReceiveResult & { grnId: string }> =>
    ReparoApi({ method: 'POST', url: '/warehouse-ops/material-stores/receive', body: JSON.stringify({ ...context, receipt }) });

export const reverseStoreGrn = (id: string, reason: string): Promise<{ eventIds: string[]; reversedCount: number }> =>
    ReparoApi({ method: 'POST', url: `/warehouse-ops/grns/${id}/reverse`, body: JSON.stringify({ reason }) });
